import { FC, useEffect, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
	useGetActions,
	useGetAllActions,
	useCreateAction,
	useUpdateAction,
	useDeleteAction,
	ActionRequest,
	ActionUpdateRequest,
} from "../../hooks/useActions";
import { IAction } from "../../domain/action.interface";
import { ErrorResponse } from "../../domain/error.interface";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { useUserStore } from "../../stores/user.store";

type ActionFormErrors = Partial<
	Record<"code" | "name" | "description" | "type" | "order", string>
>;

const ActionsComponent: FC = () => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingAction, setEditingAction] = useState<IAction | null>(null);
	// `searchInput` is the immediate text-field value; `search` is the debounced
	// term that drives the query (SIMS-6516) so we don't refetch on every keystroke.
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [formErrors, setFormErrors] = useState<ActionFormErrors>({});

	const [formData, setFormData] = useState<ActionRequest>({
		code: "",
		name: "",
		description: "",
		type: "MENU_OPTION",
		link: "",
		order: 0,
		subActions: [],
		createdBy: userInfo?.id || "SYSTEM_USER",
	});

	const {
		data: actionsResponse,
		isLoading,
		error,
		refetch,
	} = useGetActions(search || undefined, page + 1, pageSize);
	const createMutation = useCreateAction();
	const updateMutation = useUpdateAction();
	const deleteMutation = useDeleteAction();

	// Debounce the search term: update the query (and reset to page 1) only after
	// the user pauses typing, instead of firing a request per keystroke.
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setSearch(searchInput);
			setPage(0);
		}, 300);
		return () => clearTimeout(timeoutId);
	}, [searchInput]);

	const actions = actionsResponse?.body?.data || [];
	const totalCount = actionsResponse?.body?.pagination?.totalCount || 0;

	// All actions (unpaginated), used both to resolve the selected sub-actions and
	// to offer new ones.
	const { data: allActionsResponse } = useGetAllActions();
	// Resolve the selected sub-actions and the pick options. Memoized so the Map
	// + filters are not rebuilt on unrelated re-renders (e.g. form keystrokes).
	const editingActionId = editingAction?._id;
	const { selectedSubActions, subActionOptions } = useMemo(() => {
		const allActions = allActionsResponse?.body?.data || [];
		const actionsById = new Map(
			allActions.map((action) => [action._id, action]),
		);
		// Currently-selected children, resolved from the full set (NOT from the
		// filtered pick list) so an already-saved sub-action always shows as
		// selected even if it wouldn't be offered as a new pick (e.g. a
		// non-MENU_OPTION type).
		const selected = (formData.subActions || [])
			.map((id) => actionsById.get(id))
			.filter((action): action is IAction => Boolean(action));
		// Options for NEW picks: only MENU_OPTION actions (other types aren't menu
		// items), excluding the action being edited (it can't be its own child).
		// Already-selected children are unioned in so MUI can render them as chips.
		const pickable = allActions.filter(
			(candidate) =>
				candidate.type === "MENU_OPTION" && candidate._id !== editingActionId,
		);
		const options = [
			...pickable,
			...selected.filter(
				(sel) => !pickable.some((option) => option._id === sel._id),
			),
		];
		return { selectedSubActions: selected, subActionOptions: options };
	}, [allActionsResponse, formData.subActions, editingActionId]);

	const clearFieldError = (field: keyof ActionFormErrors): void => {
		setFormErrors((prevErrors) => {
			if (!prevErrors[field]) {
				return prevErrors;
			}

			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const validateForm = (): boolean => {
		const nextErrors: ActionFormErrors = {};

		if (!formData.code.trim()) {
			nextErrors.code = "Code is required";
		}

		if (!formData.name.trim()) {
			nextErrors.name = "Name is required";
		}

		if (!formData.description.trim()) {
			nextErrors.description = "Description is required";
		}

		if (!formData.type.trim()) {
			nextErrors.type = "Type is required";
		}

		if (!Number.isInteger(formData.order) || formData.order < 0) {
			nextErrors.order = "Order must be a non-negative integer";
		}

		setFormErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
		const errorResponse = error as ErrorResponse & {
			message?: string | string[];
		};

		if (
			Array.isArray(errorResponse.message) &&
			errorResponse.message.length > 0
		) {
			return errorResponse.message[0];
		}

		if (
			typeof errorResponse.message === "string" &&
			errorResponse.message.trim().length > 0
		) {
			return errorResponse.message;
		}

		if (errorResponse.error?.errorMessage) {
			return errorResponse.error.errorMessage;
		}

		return fallbackMessage;
	};

	const handleCreateAction = async () => {
		if (!validateForm()) {
			return;
		}

		try {
			await createMutation.mutateAsync(formData);
			handleCloseForm();
			refetch();
		} catch (error) {
			showSnackBar(getErrorMessage(error, "Failed to create action"), "error");
		}
	};

	const handleUpdateAction = async () => {
		if (!editingAction) return;
		if (!validateForm()) return;

		try {
			const updateData: ActionUpdateRequest = {
				...formData,
				_id: editingAction._id,
				updatedBy: userInfo?.id || "SYSTEM_USER",
			};
			await updateMutation.mutateAsync(updateData);
			handleCloseForm();
			refetch();
		} catch (error) {
			showSnackBar(getErrorMessage(error, "Failed to update action"), "error");
		}
	};

	const handleDeleteAction = async (actionId: string) => {
		if (window.confirm("Are you sure you want to delete this action?")) {
			try {
				await deleteMutation.mutateAsync(actionId);
				refetch();
			} catch (error) {
				showSnackBar(
					getErrorMessage(error, "Failed to delete action"),
					"error",
				);
			}
		}
	};

	const handleEditAction = (action: IAction) => {
		setEditingAction(action);
		setFormErrors({});
		setFormData({
			code: action.code,
			name: action.name,
			description: action.description,
			type: action.type,
			link: action.link,
			order: action.order,
			// subActions come back either populated (objects) or as raw ObjectId
			// strings — Mongoose's populate leaves unresolved refs as ids — so
			// normalize both to id strings.
			subActions: ((action.subActions ?? []) as Array<string | IAction>).map(
				(sub) => (typeof sub === "string" ? sub : sub._id),
			),
			createdBy: userInfo?.id || "SYSTEM_USER",
		});
		setShowCreateForm(true);
	};

	const handleCloseForm = () => {
		setShowCreateForm(false);
		setEditingAction(null);
		setFormErrors({});
		setFormData({
			code: "",
			name: "",
			description: "",
			type: "MENU_OPTION",
			link: "",
			order: 0,
			subActions: [],
			createdBy: userInfo?.id || "SYSTEM_USER",
		});
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// Update the immediate value only; the debounce effect syncs `search`.
		setSearchInput(event.target.value);
	};

	const handlePageChange = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPageSize(parseInt(event.target.value, 10));
		setPage(0);
	};

	if (isLoading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="400px"
			>
				<Typography>Loading actions...</Typography>
			</Box>
		);
	}

	if (error) {
		return (
			<Box>
				<Typography color="error">
					Error loading actions: {String(error)}
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4">Actions Management</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => setShowCreateForm(true)}
				>
					Add New Action
				</Button>
			</Box>

			{/* Search Bar */}
			<Box mb={3}>
				<TextField
					fullWidth
					placeholder="Search actions by name, description, or code..."
					value={searchInput}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<SearchIcon sx={{ mr: 1, color: "action.active" }} />
						),
					}}
				/>
			</Box>

			{/* Create/Edit Form */}
			{showCreateForm && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{editingAction ? "Edit Action" : "Create New Action"}
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Code"
									value={formData.code}
									onChange={(e) => {
										setFormData({ ...formData, code: e.target.value });
										clearFieldError("code");
									}}
									required
									error={Boolean(formErrors.code)}
									helperText={formErrors.code}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Name"
									value={formData.name}
									onChange={(e) => {
										setFormData({ ...formData, name: e.target.value });
										clearFieldError("name");
									}}
									required
									error={Boolean(formErrors.name)}
									helperText={formErrors.name}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Description"
									value={formData.description}
									onChange={(e) => {
										setFormData({ ...formData, description: e.target.value });
										clearFieldError("description");
									}}
									multiline
									rows={3}
									required
									error={Boolean(formErrors.description)}
									helperText={formErrors.description}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Type"
									value={formData.type}
									onChange={(e) => {
										setFormData({ ...formData, type: e.target.value });
										clearFieldError("type");
									}}
									placeholder="Enter type (e.g. MENU_OPTION, SPECIAL_FEATURE, OTHER)"
									error={Boolean(formErrors.type)}
									helperText={formErrors.type}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Link"
									value={formData.link}
									onChange={(e) =>
										setFormData({ ...formData, link: e.target.value })
									}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Order"
									type="number"
									value={Number.isNaN(formData.order) ? "" : formData.order}
									onChange={(e) => {
										const parsedOrder = Number.parseInt(e.target.value, 10);
										setFormData({
											...formData,
											order: Number.isNaN(parsedOrder) ? NaN : parsedOrder,
										});
										clearFieldError("order");
									}}
									error={Boolean(formErrors.order)}
									helperText={formErrors.order}
								/>
							</Grid>
							<Grid item xs={12}>
								<Autocomplete
									multiple
									options={subActionOptions}
									value={selectedSubActions}
									getOptionLabel={(option) => `${option.name} (${option.code})`}
									isOptionEqualToValue={(option, value) =>
										option._id === value._id
									}
									onChange={(_event, selected) =>
										setFormData({
											...formData,
											subActions: selected.map((item) => item._id),
										})
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Sub-actions"
											placeholder="Select child actions"
											helperText="These become this action's dropdown items in the app menu."
										/>
									)}
								/>
							</Grid>
						</Grid>
						<Box mt={2} display="flex" gap={2}>
							<Button
								variant="contained"
								color="primary"
								onClick={
									editingAction ? handleUpdateAction : handleCreateAction
								}
								disabled={createMutation.isPending || updateMutation.isPending}
							>
								{editingAction ? "Update Action" : "Create Action"}
							</Button>
							<Button variant="outlined" onClick={handleCloseForm}>
								Cancel
							</Button>
						</Box>
					</CardContent>
				</Card>
			)}

			{/* Actions Table */}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Code</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Order</TableCell>
								<TableCell>Sub Actions</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{actions.map((action) => (
								<TableRow key={action._id}>
									<TableCell>
										<Chip label={action.code} size="small" />
									</TableCell>
									<TableCell>
										<Typography variant="body2" fontWeight="medium">
											{action.name}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2" color="text.secondary">
											{action.description}
										</Typography>
									</TableCell>
									<TableCell>
										<Chip
											label={action.type}
											size="small"
											color={
												action.type === "MENU_OPTION" ? "primary" : "default"
											}
										/>
									</TableCell>
									<TableCell>{action.order}</TableCell>
									<TableCell>
										{action.subActions?.length > 0 && (
											<Chip
												label={`${action.subActions.length} sub-actions`}
												size="small"
												variant="outlined"
											/>
										)}
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											aria-label={`Edit ${action.name}`}
											onClick={() => handleEditAction(action)}
											color="primary"
										>
											<EditIcon />
										</IconButton>
										<IconButton
											size="small"
											aria-label={`Delete ${action.name}`}
											onClick={() => handleDeleteAction(action._id)}
											color="error"
											disabled={deleteMutation.isPending}
										>
											<DeleteIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25, 50]}
					component="div"
					count={totalCount}
					rowsPerPage={pageSize}
					page={page}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handlePageSizeChange}
				/>
			</Paper>
		</Box>
	);
};

export default ActionsComponent;
